import React from "react";
import Header from "../../header";
import Title from "../../title";
import Footer from "../../footer";
import "./style.scss";
import GroupDocument from "./GroupDocument";
import { GroupPageProps } from "../group-page/GroupInterfaces";
import apiService from "../../../services/api-service";
import { adjustForEmc3Format } from "../utils/helpers";
import { Document } from "./types";

const MultiregionGroupPage: React.FC<GroupPageProps> = (props) => {
  const btPath = `${process.env.REACT_APP_CDN_BASEPATH}24BT.png`;
  const [documents, setDocuments] = React.useState<Document[]>([]);
  const [isAllBlackTriangle, setIsAllBlackTriangle] =
    React.useState<boolean>(false);

  React.useEffect(() => {
    if (props.groupMetadata) {
      (window as any).dataLayer = (window as any).dataLayer || [];
      (window as any).dataLayer.push({
        event: 'group_load',
        groupId: props.groupMetadata.groupId,
        companyId: props.groupMetadata.companyId,
        piIds: props.groupMetadata.tabs.flatMap(tab => tab.piIds.map(pi => pi.piId)),
        documentIds: props.groupMetadata.tabs.flatMap(tab => tab.piIds.map(pi => pi.id)),
      });
    }
  }, [props.groupMetadata]);

  React.useEffect(() => {
    if (props.groupMetadata?.tabs) {
      var piIds = props.groupMetadata.tabs
        .map((tab) => tab.piIds.map((pi) => pi.piId))
        .flat();

      const promises = piIds.map((piId) => {
        // Fetch document and set black triangle value
        const contentPromise = apiService
          .fetchData(`${props.path}piIds/${piId}/content.html`)
          .then((res) => {
            if (res) {
              const content = adjustForEmc3Format(res.data).innerHTML;
              const isBlackTriangle = `${content}`.includes(
                'class="aisBlackTriangle"'
              );
              return { content, isBlackTriangle };
            }
          });

        // Fetch table of contents
        const tocPromise = apiService
          .fetchData(`${props.path}piIds/${piId}/toc.html`)
          .then((res) => {
            if (res) {
              return res.data;
            }
          });

        return Promise.all([contentPromise, tocPromise]).then(
          ([contentData, toc]) => ({
            piId,
            content: contentData!.content,
            isBlackTriangle: contentData!.isBlackTriangle,
            toc,
          })
        );
      });

      Promise.all(promises).then((documents) => {
        setDocuments(documents);
        setIsAllBlackTriangle(documents.every((x) => x.isBlackTriangle));
      });
    }
  }, [props.path, props.groupMetadata]);

  return (
    <div className="pi-portal-page">
      <Header />
      <div className="app-page">
        <Title title={props.groupMetadata?.title} isBT={isAllBlackTriangle} />
        {props.groupMetadata?.tabs && (
          <div
            className={
              "regionsContainer" +
              (props.groupMetadata.tabs.filter( x => x.piIds.length > 0 ).length === 1 ? " left" : "")
            }
          >
            {props.groupMetadata.tabs
              .filter( x => x.piIds.length > 0 )
              .sort((a, b) => a.regionId.localeCompare(b.regionId))
              .map((tab, tabIndex) => {
                const products = tab.piIds
                  .sort((x) => x.piId)
                  .map((pi) => ({ piId: pi.piId, title: pi.title }));

                return (
                  <table key={`table` + tabIndex}>
                    <thead>
                      <tr>
                        <td>
                          <p className="titleStyle">{tab.title}</p>
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      {products.map((product, piIndex) => {
                        const pi = documents.find(
                          (pi) => pi.piId === product.piId
                        );
                        const isBT = pi ? pi.isBlackTriangle : false;

                        return (
                          <tr key={piIndex}>
                            <td>
                              <a href={`#product-${tabIndex}${piIndex}`}>
                                <span className="productTableItem">
                                  <img
                                    className={
                                      (isBT
                                        ? "blacktriangle-show"
                                        : "blacktriangle-hide") +
                                      " margin-right"
                                    }
                                    src={btPath}
                                    width="20"
                                    height="20"
                                    alt="black triangle"
                                  />
                                  {product.title}
                                </span>
                              </a>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })}
          </div>
        )}
        {props.preview && (
          <div className="noticePreview">
            THIS INFORMATION IS NOT LIVE. THIS IS A PREVIEW
          </div>
        )}
        {props.companyMetadata && props.groupMetadata && (
          <div className="documentsContainer">
            {props.groupMetadata.tabs
              .sort((a, b) => a.regionId.localeCompare(b.regionId))
              .map((tab, tabIndex) =>
                tab.piIds
                  .sort((x) => x.piId)
                  .map((pi, piIndex) => {
                    return (
                      <div className="document" key={`document` + piIndex}>
                        <GroupDocument
                          productId={`product-${tabIndex}${piIndex}`}
                          document={documents.find((x) => x.piId === pi.piId)}
                          piProperties={pi}
                          aeText={tab.adverseEvent}
                          company={props.companyMetadata}
                        />
                      </div>
                    );
                  })
              )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default MultiregionGroupPage;
